import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import {
  ChartBar, Eye, Users, ArrowSquareOut, Clock, TrendUp, Globe, CaretLeft,
  ArrowClockwise, CursorClick, FileText, LinkSimple, MagnifyingGlass,
  ShareNetwork, Envelope, Desktop, DeviceMobile, DeviceTablet,
  LinkedinLogo, PencilSimple, FloppyDisk, Timer, SignOut,
  MapPin, Notebook, UserList, Briefcase, ChatCircle, Trash
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GSC_URL = 'https://search.google.com/u/1/search-console/performance/search-analytics?resource_id=https%3A%2F%2Fwww.nsecltd.co.uk%2F';
const PAGE_LABELS = { '/': 'Home', '/about': 'About', '/services': 'Services', '/careers': 'Careers', '/contact': 'Contact', '/news': 'News', '/community': 'Community', '/testimonials': 'Testimonials', '/login': 'Login', '/register': 'Register', '/admin': 'Admin', '/intranet': 'Intranet', '/analytics': 'Analytics' };
const SOURCE_ICONS = { 'Direct': LinkSimple, 'Organic Search': MagnifyingGlass, 'Social Media': ShareNetwork, 'Referral': Globe, 'Email': Envelope };
const SOURCE_COLORS = { 'Direct': '#65a30d', 'Organic Search': '#059669', 'Social Media': '#0d9488', 'Referral': '#6366f1', 'Email': '#f59e0b' };
const PIE_COLORS = ['#65a30d', '#059669', '#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6'];
const DEVICE_ICONS = { 'Desktop': Desktop, 'Mobile': DeviceMobile, 'Tablet': DeviceTablet };

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function AnalyticsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [submissionsData, setSubmissionsData] = useState(null);
  const [visitorsData, setVisitorsData] = useState(null);
  const [period, setPeriod] = useState(30);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [socialEdit, setSocialEdit] = useState(false);
  const [socialForm, setSocialForm] = useState({ linkedin: {}, instagram: {} });

  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, navigate, loading]);

  const load = async () => {
    setFetching(true);
    try {
      const dateParams = customMode && dateFrom && dateTo ? `date_from=${dateFrom}&date_to=${dateTo}` : `days=${period}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const [r, subR, visR] = await Promise.all([
        axios.get(`${API}/analytics/overview?${dateParams}`, { headers }),
        axios.get(`${API}/analytics/submissions?${dateParams}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/visitors?${dateParams}`, { headers }).catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setSubmissionsData(subR.data);
      setVisitorsData(visR.data);
      if (r.data.social) setSocialForm({ linkedin: r.data.social.linkedin || {}, instagram: r.data.social.instagram || {} });
    } catch { toast.error('Failed to load analytics'); }
    setFetching(false);
  };

  useEffect(() => { if (user) load(); }, [user, period, customMode ? `${dateFrom}-${dateTo}` : period]);

  // Listen for reload events from child components (e.g., after scrape)
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('analytics-reload', handler);
    return () => window.removeEventListener('analytics-reload', handler);
  }, []);

  const handleCustomApply = () => { if (dateFrom && dateTo) load(); };

  const [resetting, setResetting] = useState(false);
  const handleReset = async () => {
    setResetting(true);
    try {
      const r = await axios.delete(`${API}/admin/analytics/reset`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Analytics reset — removed ${r.data.removed} records`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setResetting(false);
  };
  const handleSaveSocial = async () => {
    try {
      await axios.put(`${API}/admin/analytics/social`, socialForm, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Social stats saved');
      setSocialEdit(false);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const chartData = useMemo(() => (data?.daily_views || []).map(d => ({ date: new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }), views: d.views, unique: d.unique })), [data]);
  const sourcesData = useMemo(() => (data?.sources || []).map(s => ({ name: s.source, value: s.count })), [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent" /></div>;
  if (!user) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'pages', label: 'Page Analytics' },
    { id: 'submissions', label: 'Form Submissions' },
    { id: 'visitors', label: 'Visitor Locations' },
    { id: 'sources', label: 'Traffic Sources' },
    { id: 'social', label: 'Social Media' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="analytics-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="analytics-back"><CaretLeft size={20} /></Link>
              <div>
                <h1 className="font-outfit font-bold text-xl text-slate-900 flex items-center gap-2"><ChartBar size={22} weight="duotone" className="text-lime-600" />Website Analytics</h1>
                <p className="text-xs text-slate-500">Internal traffic tracking & insights</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!customMode ? (
                <select value={period} onChange={e => setPeriod(Number(e.target.value))} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white" data-testid="analytics-period-select">
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last 12 months</option>
                </select>
              ) : (
                <div className="flex items-center gap-2">
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" data-testid="date-from" />
                  <span className="text-xs text-slate-400">to</span>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" data-testid="date-to" />
                  <Button size="sm" onClick={handleCustomApply} className="h-8 bg-lime-600 hover:bg-lime-700 text-xs" data-testid="custom-apply">Apply</Button>
                </div>
              )}
              <button onClick={() => { setCustomMode(!customMode); }} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600" data-testid="toggle-custom-date">
                {customMode ? 'Presets' : 'Custom'}
              </button>
              <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="analytics-refresh"><ArrowClockwise size={18} /></button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                    title="Reset analytics history"
                    data-testid="analytics-reset-btn"
                  >
                    <Trash size={18} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset analytics history?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="text-sm text-slate-600 space-y-2">
                        <p>This permanently deletes <strong>all</strong> page-view records collected so far. Once cleared, tracking begins fresh from the moment you confirm — perfect for cutting over from test traffic to live visitor metrics.</p>
                        <p>This action <strong>cannot be undone</strong> and is not covered by the nightly backups (analytics history is not included in snapshots).</p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="analytics-reset-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleReset}
                      disabled={resetting}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="analytics-reset-confirm"
                    >
                      {resetting ? 'Resetting…' : 'Yes — reset analytics'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <a href={GSC_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-lime-600 text-white text-sm font-medium rounded-lg hover:bg-lime-700 transition-colors" data-testid="gsc-link">
                <Globe size={16} />Google Search Console<ArrowSquareOut size={14} />
              </a>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === t.id ? 'bg-slate-50 text-lime-700 border-b-2 border-lime-600' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {fetching && !data ? (
          <div className="py-24 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto" /><p className="text-sm text-slate-500 mt-3">Loading analytics...</p></div>
        ) : data ? (
          <>
            {activeTab === 'overview' && <OverviewTab data={data} chartData={chartData} />}
            {activeTab === 'pages' && <PagesTab data={data} />}
            {activeTab === 'submissions' && <SubmissionsTab data={submissionsData} />}
            {activeTab === 'visitors' && <VisitorsTab data={visitorsData} />}
            {activeTab === 'sources' && <SourcesTab data={data} sourcesData={sourcesData} />}
            {activeTab === 'social' && <SocialTab data={data} socialEdit={socialEdit} setSocialEdit={setSocialEdit} socialForm={socialForm} setSocialForm={setSocialForm} handleSaveSocial={handleSaveSocial} />}
          </>
        ) : (
          <div className="py-24 text-center text-slate-500">Failed to load analytics data</div>
        )}
      </div>
    </div>
  );
}

/* ==================== OVERVIEW TAB ==================== */
function OverviewTab({ data, chartData }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="analytics-stats">
        <StatCard icon={Eye} label="Total Page Views" value={data.total_views} color="lime" />
        <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors} color="emerald" />
        <StatCard icon={CursorClick} label="Views Today" value={data.views_today} color="teal" />
        <StatCard icon={TrendUp} label="Bounce Rate" value={`${data.bounce_rate}%`} color="amber" />
        <StatCard icon={Timer} label="Avg Duration" value={formatDuration(data.avg_session_duration)} color="slate" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analytics-secondary-stats">
        <StatCard icon={Users} label="Unique Visitors Today" value={data.unique_visitors_today ?? 0} color="emerald" />
        <StatCard icon={ArrowClockwise} label="Returning Visitors" value={data.returning_visitors ?? 0} color="teal" />
        <StatCard icon={FileText} label="Pages / Session" value={(data.pages_per_session ?? 0).toFixed(2)} color="lime" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="analytics-traffic-chart">
        <div className="flex items-center justify-between mb-4">
          <div><h2 className="font-outfit font-semibold text-slate-900">Traffic Over Time</h2><p className="text-xs text-slate-500">Page views and unique visitors per day</p></div>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-lime-500" /> Views</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Unique</span>
          </div>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              <Line type="monotone" dataKey="views" stroke="#84cc16" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="unique" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <EmptyChart />}
      </div>

      {/* Quick Source + Device Breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-outfit font-semibold text-slate-900 mb-1">Traffic Sources</h2>
          <p className="text-xs text-slate-500 mb-4">Breakdown by source type</p>
          {data.sources?.length > 0 ? (
            <div className="space-y-2">{data.sources.slice(0, 5).map((s, i) => {
              const Icon = SOURCE_ICONS[s.source] || Globe;
              return (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-slate-700 flex items-center gap-2"><Icon size={14} style={{ color: SOURCE_COLORS[s.source] || '#94a3b8' }} />{s.source}</span>
                  <span className="text-sm font-medium text-slate-900">{s.count}</span>
                </div>
              );
            })}</div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No source data yet</p>}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-outfit font-semibold text-slate-900 mb-1">Devices</h2>
          <p className="text-xs text-slate-500 mb-4">Visitor device breakdown</p>
          {data.devices?.length > 0 ? (
            <div className="space-y-3">{data.devices.map((d, i) => {
              const Icon = DEVICE_ICONS[d.device] || Desktop;
              const total = data.devices.reduce((s, x) => s + x.count, 0);
              const pct = total > 0 ? Math.round(d.count / total * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-700 flex items-center gap-2"><Icon size={16} className="text-slate-400" />{d.device}</span>
                    <span className="text-xs text-slate-500">{pct}% ({d.count})</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-lime-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}</div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No device data yet</p>}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="seo-portal-card">
          <h2 className="font-outfit font-semibold text-slate-900 mb-1">SEO & Search</h2>
          <p className="text-xs text-slate-500 mb-4">Google Search Console</p>
          <a href={GSC_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-900 text-white text-sm font-medium rounded-lg hover:bg-neutral-800 transition-colors" data-testid="gsc-portal-link">
            <Globe size={16} />Open GSC<ArrowSquareOut size={14} />
          </a>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-slate-500">Impressions</p><p className="font-semibold text-slate-900 mt-0.5">View on GSC</p></div>
            <div className="p-3 rounded-lg bg-slate-50"><p className="text-slate-500">CTR</p><p className="font-semibold text-slate-900 mt-0.5">View on GSC</p></div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ==================== PAGES TAB ==================== */
function PagesTab({ data }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="page-analytics">
      <div className="p-6 border-b border-slate-100">
        <h2 className="font-outfit font-semibold text-slate-900">Page-Level Analytics</h2>
        <p className="text-xs text-slate-500">Detailed metrics for each page</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="text-left py-3 px-4 font-medium text-slate-600">Page</th>
            <th className="text-right py-3 px-4 font-medium text-slate-600">Views</th>
            <th className="text-right py-3 px-4 font-medium text-slate-600">Unique Visitors</th>
            <th className="text-right py-3 px-4 font-medium text-slate-600">Avg. Time on Page</th>
            <th className="text-right py-3 px-4 font-medium text-slate-600">Bounce Rate</th>
            <th className="text-right py-3 px-4 font-medium text-slate-600">Exit Rate</th>
          </tr></thead>
          <tbody>
            {(data.top_pages || []).map((p, i) => (
              <tr key={p.path} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`page-row-${i}`}>
                <td className="py-3 px-4 flex items-center gap-2">
                  <FileText size={14} className="text-slate-400 shrink-0" />
                  <span className="font-medium text-slate-800">{PAGE_LABELS[p.path] || p.path}</span>
                  <span className="text-[10px] text-slate-400">{p.path}</span>
                </td>
                <td className="text-right py-3 px-4 font-semibold text-slate-900">{p.views}</td>
                <td className="text-right py-3 px-4 text-slate-700">{p.unique_visitors}</td>
                <td className="text-right py-3 px-4 text-slate-700">{formatDuration(p.avg_duration)}</td>
                <td className="text-right py-3 px-4"><MetricBadge value={p.bounce_rate} suffix="%" thresholds={[30, 60]} /></td>
                <td className="text-right py-3 px-4"><MetricBadge value={p.exit_rate} suffix="%" thresholds={[40, 70]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!data.top_pages || data.top_pages.length === 0) && <p className="text-sm text-slate-400 text-center py-8">No page data yet</p>}
      </div>
    </div>
  );
}

/* ==================== SOURCES TAB ==================== */
function SourcesTab({ data, sourcesData }) {
  return (
    <>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Source Pie Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="source-chart">
          <h2 className="font-outfit font-semibold text-slate-900 mb-1">Traffic Source Distribution</h2>
          <p className="text-xs text-slate-500 mb-4">How visitors find your website</p>
          {sourcesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={sourcesData} cx="50%" cy="50%" outerRadius={100} innerRadius={50} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {sourcesData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-12">No source data yet</p>}
        </div>

        {/* Source Breakdown Table */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="source-breakdown">
          <h2 className="font-outfit font-semibold text-slate-900 mb-1">Source Breakdown</h2>
          <p className="text-xs text-slate-500 mb-4">Detailed traffic source analysis</p>
          <div className="space-y-3">
            {(data.sources || []).map((s, i) => {
              const Icon = SOURCE_ICONS[s.source] || Globe;
              const total = data.total_views || 1;
              const pct = Math.round(s.count / total * 100);
              return (
                <div key={i} data-testid={`source-row-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 flex items-center gap-2"><Icon size={16} style={{ color: SOURCE_COLORS[s.source] || '#94a3b8' }} />{s.source}</span>
                    <span className="text-sm text-slate-900">{s.count} <span className="text-slate-400 text-xs">({pct}%)</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: SOURCE_COLORS[s.source] || '#94a3b8' }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Referrer Domains */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="referrer-domains">
        <h2 className="font-outfit font-semibold text-slate-900 mb-1">Referring Domains</h2>
        <p className="text-xs text-slate-500 mb-4">Specific websites sending traffic to you</p>
        {data.referrer_domains?.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.referrer_domains.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`domain-${i}`}>
                <span className="text-sm text-slate-700 flex items-center gap-2"><LinkSimple size={14} className="text-slate-400" />{d.domain}</span>
                <span className="text-sm font-semibold text-slate-900 bg-white px-2 py-0.5 rounded">{d.count}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 text-center py-4">No referral data yet. Referrer domains appear when visitors arrive from external links.</p>}
      </div>

      {/* UTM Campaigns */}
      {data.sources?.some(s => s.source.startsWith('Campaign:')) && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="utm-campaigns">
          <h2 className="font-outfit font-semibold text-slate-900 mb-1">Campaign Traffic</h2>
          <p className="text-xs text-slate-500 mb-4">Traffic from UTM-tagged campaigns</p>
          <div className="space-y-2">
            {data.sources.filter(s => s.source.startsWith('Campaign:')).map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50">
                <span className="text-sm text-slate-700">{s.source.replace('Campaign: ', '')}</span>
                <span className="text-sm font-semibold text-slate-900">{s.count} visits</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ==================== SUBMISSIONS TAB ==================== */
function SubmissionsTab({ data }) {
  const [expandedForm, setExpandedForm] = useState(null);

  if (!data) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto" /><p className="text-sm text-slate-500 mt-3">Loading submissions...</p></div>;

  const allForms = [...(data.contact_forms || []), ...(data.job_applications || [])];
  const trendData = (data.daily_trend || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    contacts: d.contacts,
    applications: d.applications
  }));

  return (
    <>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="submissions-stats">
        <StatCard icon={Notebook} label="Total Submissions" value={data.total_submissions} color="lime" />
        <StatCard icon={ChatCircle} label="Contact Enquiries" value={data.total_contacts} color="emerald" />
        <StatCard icon={Briefcase} label="Job Applications" value={data.total_applications} color="teal" />
      </div>

      {/* Trend Chart */}
      {trendData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="submissions-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div><h2 className="font-outfit font-semibold text-slate-900">Submission Trend</h2><p className="text-xs text-slate-500">Daily form submissions over selected period</p></div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Contact Forms</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-lime-500" /> Job Applications</span>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="contacts" fill="#059669" name="Contact Forms" radius={[3, 3, 0, 0]} />
                <Bar dataKey="applications" fill="#65a30d" name="Job Applications" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Form Breakdown Table */}
      <div className="bg-white rounded-xl border border-slate-200" data-testid="submissions-breakdown">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-outfit font-semibold text-slate-900">Submissions by Form</h2>
          <p className="text-xs text-slate-500 mt-0.5">Click a row to see recent submissions</p>
        </div>
        {allForms.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {allForms.map((form, idx) => (
              <div key={idx}>
                <button onClick={() => setExpandedForm(expandedForm === idx ? null : idx)} className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left" data-testid={`form-row-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${form.form_type === 'contact' ? 'bg-emerald-50' : 'bg-lime-50'}`}>
                      {form.form_type === 'contact' ? <ChatCircle size={18} className="text-emerald-600" /> : <Briefcase size={18} className="text-lime-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{form.form_name}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide">{form.form_type === 'contact' ? 'Contact Enquiry' : 'Job Application'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">{form.count}</p>
                      <p className="text-[10px] text-slate-400">submissions</p>
                    </div>
                    <div className={`w-20 h-2 bg-slate-100 rounded-full overflow-hidden`}>
                      <div className={`h-full rounded-full ${form.form_type === 'contact' ? 'bg-emerald-500' : 'bg-lime-500'}`} style={{ width: `${Math.min(100, (form.count / Math.max(1, data.total_submissions)) * 100 * 3)}%` }} />
                    </div>
                  </div>
                </button>
                {expandedForm === idx && form.recent?.length > 0 && (
                  <div className="px-4 pb-4">
                    <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-100/50">
                          <tr>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500">Name</th>
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500">Email</th>
                            {form.form_type === 'contact' && <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500">Company</th>}
                            {form.form_type === 'application' && <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500">Status</th>}
                            <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.recent.map((sub, si) => (
                            <tr key={si} className="border-t border-slate-100">
                              <td className="px-3 py-2 text-xs text-slate-900">{sub.name}</td>
                              <td className="px-3 py-2 text-xs text-slate-500">{sub.email}</td>
                              {form.form_type === 'contact' && <td className="px-3 py-2 text-xs text-slate-500">{sub.company || '-'}</td>}
                              {form.form_type === 'application' && <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${sub.status === 'hired' ? 'bg-emerald-100 text-emerald-700' : sub.status === 'rejected' ? 'bg-red-100 text-red-700' : sub.status === 'shortlisted' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{sub.status}</span></td>}
                              <td className="px-3 py-2 text-xs text-slate-400">{sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-slate-500">
            <Notebook size={36} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm">No form submissions in this period</p>
            <p className="text-xs text-slate-400 mt-1">Submissions from the Contact form and Careers page will appear here</p>
          </div>
        )}
      </div>
    </>
  );
}

/* ==================== VISITORS / LOCATIONS TAB ==================== */
function VisitorsTab({ data }) {
  const [viewMode, setViewMode] = useState('locations');

  if (!data) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto" /><p className="text-sm text-slate-500 mt-3">Loading visitor data...</p></div>;

  return (
    <>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="visitors-stats">
        <StatCard icon={UserList} label="Unique IPs" value={data.total_ips} color="lime" />
        <StatCard icon={Eye} label="Total Visits" value={data.total_visits} color="emerald" />
        <StatCard icon={MapPin} label="Locations" value={data.locations?.length || 0} color="teal" />
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        {[
          { id: 'locations', label: 'By Location' },
          { id: 'countries', label: 'By Country' },
          { id: 'log', label: 'Activity Log' },
        ].map(v => (
          <button key={v.id} onClick={() => setViewMode(v.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === v.id ? 'bg-lime-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`} data-testid={`visitors-view-${v.id}`}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Location Breakdown */}
      {viewMode === 'locations' && (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="visitors-locations">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-outfit font-semibold text-slate-900">Visitor Locations</h2>
            <p className="text-xs text-slate-500 mt-0.5">Where your website visitors are browsing from</p>
          </div>
          {data.locations?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Location</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Country</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Visitors</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Page Views</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 w-36">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {data.locations.map((loc, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`location-row-${i}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-lime-600 shrink-0" />
                          <span className="text-sm font-medium text-slate-900">{loc.location}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{loc.country_code ? <span className="inline-flex items-center gap-1.5"><span className="text-xs">{loc.country_code}</span>{loc.country}</span> : loc.country}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">{loc.visitors}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{loc.visits}</td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-lime-500 rounded-full" style={{ width: `${Math.min(100, (loc.visits / Math.max(1, data.total_visits)) * 100)}%` }} /></div><span className="text-[10px] text-slate-500 w-9 text-right">{((loc.visits / Math.max(1, data.total_visits)) * 100).toFixed(1)}%</span></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-500"><MapPin size={36} className="mx-auto text-slate-300 mb-2" /><p className="text-sm">No location data available</p></div>
          )}
        </div>
      )}

      {/* Country Breakdown */}
      {viewMode === 'countries' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="visitors-countries-chart">
            <h2 className="font-outfit font-semibold text-slate-900 mb-4">Visitors by Country</h2>
            {data.countries?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.countries.map(c => ({ name: c.country, value: c.visits }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {data.countries.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="py-12 text-center text-slate-400 text-sm">No country data</div>}
          </div>
          <div className="bg-white rounded-xl border border-slate-200" data-testid="visitors-countries-table">
            <div className="p-4 border-b border-slate-100"><h3 className="font-semibold text-slate-900 text-sm">Country Breakdown</h3></div>
            <div className="divide-y divide-slate-100">
              {(data.countries || []).map((c, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2"><Globe size={14} className="text-slate-400" /><span className="text-sm font-medium text-slate-900">{c.country}</span><span className="text-[10px] text-slate-400">{c.country_code}</span></div>
                  <div className="flex items-center gap-3"><span className="text-xs text-slate-500">{c.visitors} visitors</span><span className="text-sm font-semibold text-slate-900">{c.visits} views</span></div>
                </div>
              ))}
              {!data.countries?.length && <div className="p-8 text-center text-slate-400 text-sm">No data</div>}
            </div>
          </div>
        </div>
      )}

      {/* Activity Log */}
      {viewMode === 'log' && (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="visitors-activity-log">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-outfit font-semibold text-slate-900">Visitor Activity Log</h2>
            <p className="text-xs text-slate-500 mt-0.5">Recent visitor sessions with IP, location, and browsing activity</p>
          </div>
          {data.activity_log?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">IP Address</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Location</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">ISP / Network</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Visits</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600">Pages</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">First Seen</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {data.activity_log.map((entry, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`activity-log-${i}`}>
                      <td className="px-4 py-3"><span className="text-xs font-mono text-slate-700 bg-slate-50 px-2 py-0.5 rounded">{entry.ip}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <MapPin size={12} className="text-lime-600 shrink-0" />
                          <span className="text-sm text-slate-900">{entry.city}{entry.region ? `, ${entry.region}` : ''}</span>
                          <span className="text-[10px] text-slate-400">{entry.country_code}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[200px]">{entry.isp || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">{entry.visits}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{entry.unique_pages}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{entry.first_seen ? new Date(entry.first_seen).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{entry.last_seen ? new Date(entry.last_seen).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-500"><UserList size={36} className="mx-auto text-slate-300 mb-2" /><p className="text-sm">No visitor activity recorded yet</p></div>
          )}
        </div>
      )}

      <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
        <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">About visitor locations:</span> IP addresses are geolocated to approximate city-level locations. Accuracy varies — VPN users and corporate networks may show different locations. Data is cached to improve performance.</p>
      </div>
    </>
  );
}

/* ==================== SOCIAL TAB ==================== */
function SocialTab({ data, socialEdit, setSocialEdit, socialForm, setSocialForm, handleSaveSocial }) {
  const social = data?.social || {};
  const li = social.linkedin || {};
  const updateLi = (k, v) => setSocialForm(f => ({ ...f, linkedin: { ...f.linkedin, [k]: v } }));

  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const lastScrape = social.last_scrape;

  useEffect(() => {
    if (!configLoaded) {
      axios.get(`${API}/admin/analytics/social/config`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(r => {
          setLinkedinUrl(r.data.linkedin_url || 'https://www.linkedin.com/company/18265664/admin/dashboard/');
          setConfigLoaded(true);
        })
        .catch(() => { setConfigLoaded(true); });
    }
  }, [configLoaded]);

  const handleScrape = async () => {
    setScraping(true);
    setScrapeResult(null);
    try {
      const r = await axios.post(`${API}/admin/analytics/social/scrape`, {
        linkedin_url: linkedinUrl,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setScrapeResult(r.data.results);
      const liResult = r.data.results?.linkedin || {};
      if (liResult.status === 'scraped') {
        toast.success('LinkedIn data fetched — review the updated stats below');
        window.dispatchEvent(new CustomEvent('analytics-reload'));
      } else {
        toast.info('Scrape completed but no public data was returned. You can manually enter the values.');
      }
    } catch { toast.error('Scrape request failed'); }
    setScraping(false);
  };

  const statusLabel = (status) => {
    if (!status) return null;
    const map = {
      'scraped': { text: 'Data retrieved', cls: 'bg-emerald-100 text-emerald-700' },
      'blocked_by_linkedin': { text: 'Blocked by LinkedIn', cls: 'bg-amber-100 text-amber-700' },
      'login_required': { text: 'Login required', cls: 'bg-amber-100 text-amber-700' },
      'page_loaded_but_no_data': { text: 'No public data found', cls: 'bg-slate-100 text-slate-600' },
      'no_url': { text: 'No URL configured', cls: 'bg-slate-100 text-slate-500' },
    };
    const info = map[status] || { text: status, cls: 'bg-red-100 text-red-600' };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${info.cls}`}>{info.text}</span>;
  };

  // Engagement rate = reactions / impressions × 100
  const engagementRate = (li.reactions && li.impressions)
    ? ((li.reactions / li.impressions) * 100).toFixed(2)
    : null;

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit font-semibold text-slate-900">LinkedIn Analytics</h2>
          <p className="text-xs text-slate-500">Publicly-scraped stats for the North Somerset Environment Company page — manually editable</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)} data-testid="social-config-btn">
            <Globe size={14} className="mr-1" />{showConfig ? 'Hide Config' : 'Profile URL'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleScrape} disabled={scraping} data-testid="social-scrape-btn" className="border-lime-300 text-lime-700 hover:bg-lime-50">
            <ArrowClockwise size={14} className={`mr-1 ${scraping ? 'animate-spin' : ''}`} />{scraping ? 'Fetching...' : 'Fetch Latest'}
          </Button>
          {!socialEdit ? (
            <Button variant="outline" size="sm" onClick={() => setSocialEdit(true)} data-testid="social-edit-btn"><PencilSimple size={14} className="mr-1" />Edit Manually</Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSocialEdit(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveSocial} className="bg-lime-600 hover:bg-lime-700" data-testid="social-save-btn"><FloppyDisk size={14} className="mr-1" />Save</Button>
            </div>
          )}
        </div>
      </div>

      {/* URL Configuration Panel */}
      {showConfig && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="social-config-panel">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">LinkedIn Company URL</h3>
          <p className="text-xs text-slate-500 mb-4">The public company page URL to fetch stats from.</p>
          <div>
            <Label className="text-xs font-medium text-slate-700 flex items-center gap-1.5"><LinkedinLogo size={14} weight="fill" className="text-[#0A66C2]" />LinkedIn Company URL</Label>
            <Input value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)} placeholder="https://www.linkedin.com/company/18265664/" className="mt-1 text-sm" data-testid="linkedin-url-input" />
          </div>
          <p className="text-[10px] text-slate-400 mt-3">URL is saved automatically when you click &quot;Fetch Latest&quot;. LinkedIn heavily restricts unauthenticated scraping so many numbers may need to be entered manually from the LinkedIn admin dashboard.</p>
        </div>
      )}

      {/* Scrape result status */}
      {scrapeResult && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="scrape-result">
          <h4 className="text-xs font-semibold text-slate-700 mb-2">Last Fetch Result</h4>
          <div className="flex items-center gap-2">
            <LinkedinLogo size={16} weight="fill" className="text-[#0A66C2]" />
            {statusLabel(scrapeResult.linkedin?.status)}
            {scrapeResult.linkedin?.followers != null && <span className="text-xs text-slate-600">{scrapeResult.linkedin.followers.toLocaleString()} followers</span>}
          </div>
        </div>
      )}

      {/* LinkedIn — full-width enhanced card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="linkedin-stats">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <LinkedinLogo size={32} weight="fill" className="text-[#0A66C2]" />
            <div>
              <h3 className="font-outfit font-semibold text-slate-900 text-lg">LinkedIn</h3>
              <p className="text-xs text-slate-500">North Somerset Environment Company Ltd</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="https://www.linkedin.com/company/18265664/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0A66C2] hover:text-[#084a8f] flex items-center gap-1 font-medium" data-testid="linkedin-admin-link">
              <ArrowSquareOut size={12} />Admin Dashboard
            </a>
            {linkedinUrl && (
              <a href={linkedinUrl.replace('/admin/dashboard/', '')} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                <ArrowSquareOut size={12} />Public Page
              </a>
            )}
          </div>
        </div>

        {socialEdit ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div><Label className="text-[10px]">Followers</Label><Input type="number" value={socialForm.linkedin.followers || ''} onChange={e => updateLi('followers', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Posts (last 30 days)</Label><Input type="number" value={socialForm.linkedin.posts || ''} onChange={e => updateLi('posts', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Reactions</Label><Input type="number" value={socialForm.linkedin.reactions || ''} onChange={e => updateLi('reactions', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Impressions</Label><Input type="number" value={socialForm.linkedin.impressions || ''} onChange={e => updateLi('impressions', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Comments</Label><Input type="number" value={socialForm.linkedin.comments || ''} onChange={e => updateLi('comments', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Shares</Label><Input type="number" value={socialForm.linkedin.shares || ''} onChange={e => updateLi('shares', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">Page Views (30d)</Label><Input type="number" value={socialForm.linkedin.page_views || ''} onChange={e => updateLi('page_views', Number(e.target.value))} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px]">New Followers (30d)</Label><Input type="number" value={socialForm.linkedin.new_followers || ''} onChange={e => updateLi('new_followers', Number(e.target.value))} className="h-8 text-xs" /></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <SocialStat label="Followers" value={li.followers} />
            <SocialStat label="New Followers (30d)" value={li.new_followers} />
            <SocialStat label="Posts (30d)" value={li.posts} />
            <SocialStat label="Page Views (30d)" value={li.page_views} />
            <SocialStat label="Impressions" value={li.impressions} />
            <SocialStat label="Reactions" value={li.reactions} />
            <SocialStat label="Comments" value={li.comments} />
            <SocialStat label="Shares" value={li.shares} />
          </div>
        )}

        {engagementRate && !socialEdit && (
          <div className="mt-5 flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-lime-50 to-emerald-50 border border-lime-200">
            <div className="text-3xl font-bold text-lime-700">{engagementRate}%</div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Engagement Rate</p>
              <p className="text-xs text-slate-600">Reactions ÷ Impressions × 100. Anything above 2% is considered strong on LinkedIn.</p>
            </div>
          </div>
        )}

        {lastScrape && !socialEdit && <p className="text-[10px] text-slate-400 mt-4 flex items-center gap-1"><Clock size={10} />Last fetch: {new Date(lastScrape.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — {statusLabel(lastScrape.linkedin_status)}</p>}
        {!lastScrape && social.updated_at && !socialEdit && <p className="text-[10px] text-slate-400 mt-4">Last updated: {new Date(social.updated_at).toLocaleDateString('en-GB')} by {social.updated_by || 'Admin'}</p>}
      </div>

      <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
        <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">How it works:</span> Click &quot;Fetch Latest&quot; to pull publicly available follower/post counts from LinkedIn. LinkedIn heavily restricts unauthenticated scraping, so most metrics (impressions, comments, shares, new followers, page views) will need to be copied from the LinkedIn admin dashboard using &quot;Edit Manually&quot;. The engagement rate is calculated automatically once Reactions and Impressions are populated.</p>
      </div>
    </>
  );
}

/* ==================== SHARED COMPONENTS ==================== */
function StatCard({ icon: Icon, label, value, color }) {
  const colors = { lime: 'bg-lime-50 text-lime-600', emerald: 'bg-emerald-50 text-emerald-600', teal: 'bg-teal-50 text-teal-600', amber: 'bg-amber-50 text-amber-600', slate: 'bg-slate-100 text-slate-600' };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3"><div className={`p-2 rounded-lg ${colors[color]}`}><Icon size={20} weight="duotone" /></div></div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function MetricBadge({ value, suffix = '', thresholds = [30, 60] }) {
  const color = value <= thresholds[0] ? 'text-emerald-600 bg-emerald-50' : value <= thresholds[1] ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded ${color}`}>{value}{suffix}</span>;
}

function SocialStat({ label, value }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg">
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-1">{value != null ? value.toLocaleString() : '—'}</p>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">
      <div className="text-center"><ChartBar size={40} className="mx-auto mb-2 opacity-40" /><p>No traffic data recorded yet</p><p className="text-xs mt-1">Data will appear as visitors browse the site</p></div>
    </div>
  );
}
